import { Component, ViewChild, Output, EventEmitter } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { finalize } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApiService } from 'src/app/services/api.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService } from 'src/app/services/role-loading.service'
import { ApiKeyDto } from 'src/app/services/dtos'
import { ModalService, ModalController } from '../../services/modal.service'
import { handleArpioError } from 'src/utils/handle-error'
import { ApiKeyModalBaseComponent } from '../api-key-modal-base/api-key-modal-base.component'

@Component({
  selector: 'app-new-api-key-modal',
  templateUrl: './new-api-key-modal.component.html',
  standalone: false,
})
export class NewApiKeyModalComponent extends ApiKeyModalBaseComponent implements ModalController {
  @ViewChild('newApiKeyModal') modal: ModalDialogComponent
  @ViewChild('secretDialog') secretDialog: ModalDialogComponent
  @Output() apiKeyCreated = new EventEmitter<void>()

  // For displaying the generated API key secret
  createdApiKey: ApiKeyDto | null = null
  secretCopied = false

  constructor(
    formBuilder: FormBuilder,
    accountService: AccountService,
    uiService: UiService,
    apiService: ApiService,
    accessLevelService: AccessLevelService,
    roleLoadingService: RoleLoadingService,
    private modalService: ModalService
  ) {
    super(formBuilder, accountService, uiService, apiService, accessLevelService, roleLoadingService)
    this.modalService.register(this, this)
  }

  ngOnDestroy() {
    super.ngOnDestroy()
    this.modalService.unregister(this)
  }

  async open() {
    this.resetForm()
    await this.loadAvailableRoles()
    this.modal.open()
  }

  protected resetForm() {
    super.resetForm()
    this.createdApiKey = null
  }

  // Save operation
  onSave() {
    if (!this.isFormValid || this.saving) {
      return
    }

    this.saving = true

    const formValue = this.apiKeyForm.value
    const apiKeyPayload: ApiKeyDto = {
      name: formValue.name,
      enabled: formValue.enabled !== false,
      roles: this.selectedRoles.map((r) => r.id),
    }

    this.saveSubscription = this.apiService
      .createApiKey(this.accountService.accountId, apiKeyPayload)
      .pipe(
        finalize(() => {
          this.saving = false
        })
      )
      .subscribe(
        (result: ApiKeyDto) => {
          this.createdApiKey = result
          this.modal.close()
          this.secretDialog.open()
          this.apiKeyCreated.emit()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  // Close secret dialog and the main modal
  closeSecretDialog() {
    this.secretDialog.close()
    this.close()
  }

  // Copy secret to clipboard
  copySecretToClipboard() {
    if (this.createdApiKey?.secret) {
      navigator.clipboard.writeText(this.createdApiKey.secret).then(
        () => {
          console.log('Secret copied to clipboard')
          this.secretCopied = true
          // Reset the icon back to copy after 1 second
          setTimeout(() => {
            this.secretCopied = false
          }, 1000)
        },
        (err) => {
          console.error('Failed to copy secret:', err)
        }
      )
    }
  }

  getSaveButtonText(): string {
    return this.saving ? 'Creating...' : 'Create API Key'
  }
}
